import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { EasyAvatar } from "fin-web-components";
import { siteConfig, standaloneSite } from "../../site-config/SiteConfig";
import { getStandaloneSpaceId } from "../config";
import { StoreOrderApi } from "../order/StoreOrderApi";
import type { CreateStoreOrderResult } from "../order/StoreOrderType";
import type { MembershipProduct } from "../../platform_spaces/space/SpaceType";
import "../../platform_spaces/space/SpaceStyle.css";

type Props = {
  row: MembershipProduct;
  open: boolean;
  onClose: () => void;
};

/** 独立站：非策略类产品的购买弹窗（布局参考平台站，路由走独立站收银台） */
export function StandaloneProductPurchaseModal(props: Props): JSX.Element {
  const { row, open, onClose } = props;
  const spaceId = getStandaloneSpaceId();
  const navigate = useNavigate();
  const [tierId, setTierId] = useState<string | null>(null);
  const [pendingSign, setPendingSign] = useState(false);

  const isRetail = row.pricingMode?.trim().toLowerCase() === "retail";
  const specs = isRetail ? (row.priceSpecs ?? []).filter((s) => s.enabled) : [];

  const initTierOnOpen = useCallback(() => {
    setTierId(specs.find((s) => s.enabled)?.tierId ?? specs[0]?.tierId ?? null);
  }, [specs]);

  const maybeSignAndCreate = useCallback(async (): Promise<CreateStoreOrderResult | string> => {
    const tier = tierId ?? specs[0]?.tierId;
    if (!tier) {
      return "请选择规格";
    }
    let res = await StoreOrderApi.createOrder({
      spaceId,
      spaceProductId: row.spaceProductId,
      serviceTier: tier,
    });
    if (typeof res !== "string" && res.createResult === 401) {
      const needSign = res;
      const tid = row.agreementTemplateId?.trim();
      if (!tid) {
        message.error(needSign.failMessage ?? "请先签署协议");
        return needSign;
      }
      await new Promise<void>((resolve) => {
        Modal.confirm({
          title: "签署购买协议",
          content: needSign.failMessage ?? "需先签署产品购买协议",
          okText: "去签署",
          cancelText: "取消",
          width: 400,
          centered: true,
          className: "lf-modal-square",
          onOk: async () => {
            const signRes = await StoreOrderApi.signAgreement({
              spaceId,
              spaceProductId: row.spaceProductId,
              templateId: tid,
            });
            if (typeof signRes === "string") {
              message.error(signRes);
            } else {
              message.success("签署成功");
            }
            resolve();
          },
          onCancel: () => resolve(),
        });
      });
      res = await StoreOrderApi.createOrder({
        spaceId,
        spaceProductId: row.spaceProductId,
        serviceTier: tier,
      });
    }
    return res;
  }, [row, spaceId, specs, tierId]);

  const confirmPurchase = async () => {
    if (specs.length > 0 && !tierId) {
      message.warning("请先选择规格");
      return;
    }
    setPendingSign(true);
    try {
      const res = await maybeSignAndCreate();
      if (typeof res === "string") {
        return;
      }
      if (res.createResult !== 0 || !res.orderId) {
        message.error(res.failMessage ?? "下单失败");
        return;
      }
      onClose();
      navigate(`/tactik/checkout/${encodeURIComponent(res.orderId)}`);
    } finally {
      setPendingSign(false);
    }
  };

  return (
    <Modal
      title={null}
      footer={null}
      closable={false}
      open={open}
      afterOpenChange={(visible) => {
        if (visible) {
          initTierOnOpen();
        }
      }}
      onCancel={onClose}
      width="100%"
      centered={false}
      destroyOnClose
      maskClosable
      className="space-purchase-modal space-purchase-modal--fullscreen"
      wrapClassName="space-purchase-modal-wrap"
      styles={{
        wrapper: { padding: 0 },
        content: {
          padding: 0,
          margin: 0,
          maxWidth: "100vw",
          width: "100vw",
          height: "100vh",
          borderRadius: 0,
          overflow: "hidden",
        },
        body: {
          padding: 0,
          height: "100%",
          overflow: "hidden",
        },
      }}
    >
      <div className="space-purchase-modal-shell">
        <aside className="space-purchase-modal-space-pane" aria-label="站点信息">
          <div className="space-purchase-modal-space-pane__inner">
            <div className="space-purchase-modal-space-pane__avatar-wrap">
              <EasyAvatar
                shape="square"
                squareRadius={10}
                size={104}
                url={siteConfig.shellLogo}
                word={standaloneSite.brandName.slice(0, 1)}
                alt=""
                className="space-purchase-modal-space-pane__avatar"
              />
            </div>
            <div className="space-purchase-modal-space-pane__title-row">
              <span className="space-purchase-modal-space-pane__name">
                {siteConfig.shellBrandNavLabel || standaloneSite.brandName}
              </span>
            </div>
            <p className="space-purchase-modal-space-pane__signature">
              {standaloneSite.slogan}
            </p>
            <p className="space-purchase-modal-space-pane__fans">{spaceId}</p>
          </div>
        </aside>
        <div className="space-purchase-modal-checkout-pane">
          <button
            type="button"
            className="space-purchase-modal-close"
            aria-label="关闭"
            onClick={onClose}
          >
            <CloseOutlined />
          </button>
          <div className="space-purchase-modal-checkout-scroll">
            <div className="space-purchase-modal-body__inner">
              <header className="space-purchase-modal-product-block">
                <p className="space-purchase-modal-product-title">{row.displayName}</p>
              </header>
              <section className="space-purchase-modal-intro" aria-label="产品介绍">
                {row.description != null && row.description.trim() !== "" ? (
                  <p>{row.description}</p>
                ) : (
                  <p className="space-purchase-modal-intro-placeholder">暂无详细介绍</p>
                )}
              </section>
              {specs.length > 0 ? (
                <section className="space-purchase-modal-section" aria-label="规格">
                  <p className="space-purchase-modal-label">选择规格</p>
                  <div
                    className="space-purchase-modal-tier-grid"
                    role="group"
                    aria-label="选择规格"
                  >
                    {specs.map((s) => {
                      const active = tierId === s.tierId;
                      return (
                        <button
                          key={s.tierId}
                          type="button"
                          className={
                            active
                              ? "space-purchase-tier-card space-purchase-tier-card--active"
                              : "space-purchase-tier-card"
                          }
                          onClick={() => setTierId(s.tierId)}
                        >
                          <span className="space-purchase-tier-card__name">
                            {s.specName || s.durationLabel}
                          </span>
                          <span className="space-purchase-tier-card__price">
                            ¥{s.priceYuan}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}
              <p className="space-purchase-modal-product-lead">
                开通后可按权益使用完整产品功能
              </p>
            </div>
          </div>
          <div className="space-purchase-modal-footer-strip">
            <div className="space-purchase-modal-footer">
              <Button
                className="space-purchase-modal-footer-btn"
                style={{ minWidth: 120 }}
                onClick={onClose}
              >
                取消
              </Button>
              <Button
                type="primary"
                className="space-purchase-modal-footer-btn space-purchase-modal-footer-btn--primary"
                style={{ minWidth: 120 }}
                loading={pendingSign}
                onClick={() => void confirmPurchase()}
              >
                立即订购
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
